import { applyTheming, DEFAULT_PARTNER } from "@pandas/core-ui";
import { canonicalizePartnerRoute } from "@pandas/core-ui/route";
import type { DeviceType } from "@pandas/http-client";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { servicesOrchestratorValues } from "@/config/services";
import { configuratorApi, platformApi } from "@/services";
import { useStore } from "@/store";
import { logError } from "@/utils/logging/error";
import logger from "@/utils/logging/logger";

export const Route = createFileRoute("/$locale/$partner")({
	beforeLoad: async ({ context, params, search, location }) => {
		const storedPartner = useStore.getState().location?.Partner?.code;
		const isAdmin = context.auth.user?.platform_roles?.includes("RetailApp Admin") ?? false;
		const result = await canonicalizePartnerRoute({
			pathname: location.pathname,
			search,
			locale: params.locale,
			currentPartnerSegment: params.partner,
			reservedPartnerSegments: context.reservedPartnerRouteSegments,
			resolvePartner: async (s, current) => {
				if (typeof s.partnerCode === "string") return s.partnerCode;
				// Admins navigate freely (e.g. via the store-location picker) — trust the URL.
				if (isAdmin) return current;
				if (context.auth.isAuthenticated) {
					const locationId = context.auth.user?.platform_app_metadata?.location_id;
					if (locationId) {
						try {
							const locationData = await platformApi.getLocationAndPartner(locationId);
							return locationData.Partner.code;
						} catch {
							// fall through to stored / default
						}
					}
				}
				return storedPartner ?? DEFAULT_PARTNER;
			},
		});

		if (result?.kind === "redirect") throw redirect({ href: result.href, replace: true });
		// `unresolvable` cannot occur — resolvePartner always returns DEFAULT_PARTNER as a last resort.

		if (!context.auth.isAuthenticated) return;

		const { location: storeLocation, config } = useStore.getState();
		const storedPartnerCode = storeLocation?.Partner?.code;
		const isGoingHome = location.pathname.endsWith("/home");

		if (storeLocation?.id && config && storedPartnerCode === params.partner) {
			if (!isGoingHome) return;

			// Navigating to home with an existing config: refresh theming + partner settings
			// so config changes take effect without requiring a logout.
			try {
				const partnerConfig = await configuratorApi.getConfig(storedPartnerCode);
				const theming = applyTheming(partnerConfig, storedPartnerCode, servicesOrchestratorValues.urls.cdn);
				useStore.getState().setConfig(storedPartnerCode, { theming, partnerSettings: partnerConfig });
			} catch (e) {
				logError(e, "Could not refresh partner config on home navigation");
			}
			return;
		}

		const user = context.auth.user;
		if (isAdmin) {
			if (storedPartnerCode && storedPartnerCode !== params.partner) {
				useStore.getState().clearPartnerData();
			}
			return;
		}

		const locationId = user?.platform_app_metadata?.location_id;
		if (!locationId) return;

		try {
			const locationData = await platformApi.getLocationAndPartner(locationId);
			const partnerCode = locationData.Partner.code;
			const partnerConfig = await configuratorApi.getConfig(partnerCode);
			const theming = applyTheming(partnerConfig, partnerCode, servicesOrchestratorValues.urls.cdn);
			const { setLocation, setConfig, setAttribute } = useStore.getState();
			setLocation(locationData);
			setConfig(partnerCode, { theming, partnerSettings: partnerConfig });
			setAttribute("category", (partnerConfig.supportedDeviceTypes[0]?.toLowerCase() as DeviceType) ?? "smartphone");
			logger.injectCommonProp("pandas_location_id", locationData.id);
			logger.injectCommonProp("pandas_partner_code", partnerCode);
		} catch (e) {
			logError(e, "Could not initialize partner config");
		}
	},
	component: () => <Outlet />,
});
